/**
 * PipelineDiagram — JSD → KROVEX/RoMSH 데이터 흐름 시각화 (시연용).
 *
 * 정적 다이어그램 (no props). HTML/CSS + Tailwind only, 신규 의존성 없음.
 * ADR-0001 §2.6 워크플로우 통합 지점 기반.
 */
export default function PipelineDiagram() {
  return (
    <div
      data-testid="pipeline-diagram"
      className="rounded-lg border border-gray-200 bg-white p-5"
    >
      {/* 시작: 시료 */}
      <div className="flex flex-col items-center gap-0">
        <NodeBox
          testId="node-sample"
          color="gray"
          title="시료 (GC-MS)"
          description="화재 현장 수거 시료 → GC-MS 분리 후 peaks 추출"
        />

        <EdgeLabel label="peaks (m/z, intensity, RT)" />

        {/* JSD 매칭 */}
        <NodeBox
          testId="node-jsd"
          color="blue"
          title="JSD 매칭 (인하대 SMILES_pred)"
          description="142종 참조 화합물 DB와 Jensen-Shannon Distance 기반 매칭 → top-k SMILES 동정"
        />

        {/* JSD 출력에서 두 갈래로 분기 */}
        <div className="text-gray-400 text-lg font-mono select-none my-1">
          ↓
        </div>
        <EdgeLabel label="identified compounds (SMILES, match_score, RT)" />

        {/* 분기: KROVEX (왼쪽) + 입력 성분 선정 → RoMSH (오른쪽) */}
        <div className="w-full flex flex-col md:flex-row items-start justify-center gap-4 mt-1">
          {/* 왼쪽: KROVEX */}
          <div className="flex-1 flex flex-col items-center gap-0">
            <BranchArrow direction="left" />
            <NodeBox
              testId="node-krovex"
              color="purple"
              title="KROVEX 물성 예측"
              description="성분별(per compound) 증기압·용해도 AI 예측 — 화재 위험도 평가에 활용"
            />
            <EdgeLabel label="vapor_pressure, solubility (per compound)" />
            <ResultBox testId="node-krovex-result" label="물성 결과 저장" />
          </div>

          {/* 오른쪽: 입력 성분 선정 → RoMSH */}
          <div className="flex-1 flex flex-col items-center gap-0">
            <BranchArrow direction="right" />
            <NodeBox
              testId="node-top5"
              color="blue"
              title="입력 성분 선정 (최대 8)"
              description="물질마다 대표 1건 → 8개 이하면 전량, 넘으면 매칭 상위 6 + 아직 대표되지 않은 RT 구간 2 (선정 규칙 v2.1)"
            />
            <EdgeLabel label="mixture (최대 8 SMILES + RT + 풍부도=매칭 점수 · TIC 신호 비율 입력은 검토 중)" />
            <NodeBox
              testId="node-romsh"
              color="orange"
              title="RoMSH 트랜스포머 (인하대)"
              description="시료-수준 이진 분류기 — SMILES 토큰 인코더 + Mixture Transformer (CLS pooling) + Meta Fusion"
            />
            <EdgeLabel label="probability (0~1) + label (0/1)" />
            <ResultBox
              testId="node-romsh-result"
              label="인화성 물질 패턴 분석 결과"
            />
          </div>
        </div>
      </div>

      {/* 범례 */}
      <div className="mt-5 pt-4 border-t border-gray-100 flex flex-wrap gap-4 text-xs text-gray-500">
        <LegendItem color="blue" label="인하대 AI 모델" />
        <LegendItem color="purple" label="KROVEX (물성)" />
        <LegendItem color="orange" label="RoMSH (시료 분류)" />
        <LegendItem color="gray" label="시료 / 결과" />
      </div>
    </div>
  );
}

/* ─── Sub-components ─────────────────────────────────────── */

type BoxColor = "blue" | "purple" | "orange" | "gray";

const colorMap: Record<BoxColor, string> = {
  blue: "bg-blue-50 border-blue-200 text-blue-900",
  purple: "bg-purple-50 border-purple-200 text-purple-900",
  orange: "bg-orange-50 border-orange-200 text-orange-900",
  gray: "bg-gray-50 border-gray-200 text-gray-800",
};

const titleColorMap: Record<BoxColor, string> = {
  blue: "text-blue-900",
  purple: "text-purple-900",
  orange: "text-orange-900",
  gray: "text-gray-800",
};

function NodeBox({
  testId,
  color,
  title,
  description,
}: {
  testId: string;
  color: BoxColor;
  title: string;
  description: string;
}) {
  return (
    <div
      data-testid={testId}
      className={`w-full max-w-sm rounded-lg border-2 px-4 py-3 ${colorMap[color]}`}
    >
      <div className={`text-sm font-bold mb-1 ${titleColorMap[color]}`}>
        {title}
      </div>
      <div className="text-xs leading-relaxed opacity-80">{description}</div>
    </div>
  );
}

function ResultBox({ testId, label }: { testId: string; label: string }) {
  return (
    <div
      data-testid={testId}
      className="w-full max-w-sm rounded border border-gray-300 bg-gray-100 px-4 py-2 text-center text-sm font-semibold text-gray-700"
    >
      {label}
    </div>
  );
}

function EdgeLabel({ label }: { label: string }) {
  return (
    <div className="flex flex-col items-center gap-0 my-0.5">
      <div className="text-gray-400 text-lg font-mono select-none">↓</div>
      <span className="text-xs text-gray-500 text-center max-w-xs">
        {label}
      </span>
    </div>
  );
}

function BranchArrow({ direction }: { direction: "left" | "right" }) {
  return (
    <div className="text-gray-400 text-lg font-mono select-none my-1">
      {direction === "left" ? "↙" : "↘"}
    </div>
  );
}

function LegendItem({ color, label }: { color: BoxColor; label: string }) {
  const dotMap: Record<BoxColor, string> = {
    blue: "bg-blue-400",
    purple: "bg-purple-400",
    orange: "bg-orange-400",
    gray: "bg-gray-400",
  };
  return (
    <div className="flex items-center gap-1.5">
      <div className={`w-3 h-3 rounded-sm ${dotMap[color]}`} />
      <span>{label}</span>
    </div>
  );
}
